/**
 * Geolocation Provider Interface
 * 
 * Defines the contract for IP geolocation providers.
 * Implementations can use different services (MaxMind, ip-api.com, etc.)
 */

/**
 * Confidence level of a geolocation result
 * - 'high': Location verified via multiple sources or authoritative data (e.g., CDN headers)
 * - 'medium': Location from single reliable source (e.g., reverse DNS with location code)
 * - 'low': Location uncertain (e.g., IP geolocation for anycast IP, RTT validation failed)
 */
export type GeoConfidence = 'high' | 'medium' | 'low';

/**
 * Source of geolocation data
 */
export type GeoSource = 'header' | 'dns' | 'ip-api' | 'maxmind' | 'cache' | 'fallback';

/**
 * Standardized result from any geolocation provider
 */
export interface GeoLookupResult {
    ip: string;
    city: string | null;
    country: string | null;
    countryCode: string | null;
    latitude: number;
    longitude: number;
    org: string | null;       // Organization/ISP name
    isp: string | null;       // ISP name (may be same as org)
    timezone: string | null;
    hostname: string | null;  // DNS Hostname if available
    confidence?: GeoConfidence;  // How reliable is this location
    source?: GeoSource;          // Where did this data come from
}

/**
 * Interface that all geolocation providers must implement
 */
export interface IGeoProvider {
    /** Provider name for logging/debugging */
    readonly name: string;

    /**
     * Look up the current user's IP address and location
     * @returns Location data for the requesting client's IP
     */
    lookupSelf(): Promise<GeoLookupResult>;

    /**
     * Look up location data for a specific IP address
     * @param ip - IPv4 or IPv6 address to look up
     * @returns Location data for the specified IP
     */
    lookupIp(ip: string): Promise<GeoLookupResult>;
}

/**
 * Default fallback result when lookup fails
 */
export const DEFAULT_GEO_RESULT: GeoLookupResult = {
    ip: '',
    city: null,
    country: null,
    countryCode: null,
    latitude: 50.8503,  // Brussels fallback
    longitude: 4.3517,
    org: null,
    isp: null,
    timezone: null,
    hostname: null
};
